import { h, Fragment } from 'preact';
import { useState } from 'preact/hooks';

import './index.less';
import { getCompanyId, getCompanyName } from '../../../lib/Company';
import {
  getCatalogueTheme,
  getLogoVersion,
  doesLogoExists,
  getLogoHeight,
  getLogoWidth
} from '../../../lib/Amalgam';
import {
  getAllCatalogueIds,
  shouldRemoveCatalogueCount,
  shouldRemoveGroupCompanyHeader
} from '../../../lib/GroupAmalgam';
import {
  shouldRemoveCompanyNameFromCatalogueHeader,
  shouldRemoveSingleCompanyHeader
} from '../../../lib/Experiments';
import {
  shouldHideCompanyNameFromCatalogueHeader,
  shouldCenterAlignCompanyNameAndLogoInCatalogueHeader
} from '../../../lib/CompanyExperiments';
import { isDesktopView } from '../../../lib/Utils';
import I18n from '../../../lib/I18n';
import {
  addToCartRoutes,
  NAME as ROUTE_NAME,
  isGroupRoute,
  getCurrentRouteName,
  shouldShowLightThemeOnRoute,
  isResellerRoute,
  shouldShowCustomerProfileButton
} from '../../../lib/Route';
import SearchIcon from '../../../molecules/Icon/Search';
import NavBarReviewAction from '../../../organisms/NavBarReviewAction';
import NavBarSearchAction from '../../NavBarSearchAction';
import NavBarSearchBar from '../../NavBarSearchBar';
import Menu from '../../Menu';
import BackButton from '../../../molecules/CommonBackButton';
import CustomerProfile from '../../../molecules/CustomerProfile';
import { NAVBAR_HEIGHT, NAVBAR_LARGE_LOGO_HEIGHT, NAVBAR_LOGO_HEIGHT } from '../../../../config';

const HeaderCollapsed = (props) => {
  const [addToCartActionVisible, setAddToCartActionVisible] = useState(false);

  const {
    scrollTop,
    showSearch,
    onSearch,
    goBack,
    showHeaderCollapsedBorder,
    showHeaderCollapsedShadow,
    containerStyle = {}
  } = props;
  const currentRoute = getCurrentRouteName();
  const allCatalogueIds = getAllCatalogueIds();
  const removeCompanyHeader = isGroupRoute()
    ? shouldRemoveGroupCompanyHeader()
    : shouldRemoveSingleCompanyHeader();
  const isDesktop = isDesktopView();
  const { homepage: homepageTheme, pageTextColor, homeGrid: homeGridTheme } = getCatalogueTheme();

  const headerBackgroundColor = shouldShowLightThemeOnRoute(currentRoute)
    ? '#fff'
    : homeGridTheme.backgroundColor;

  const companyNameTextColor = shouldShowLightThemeOnRoute(currentRoute) ? '#000' : pageTextColor;

  const hideCompanyNameFromCatalogueHeader = shouldHideCompanyNameFromCatalogueHeader();
  const centerAlignCompanyNameAndLogoInCatalogueHeader =
    !isDesktop && shouldCenterAlignCompanyNameAndLogoInCatalogueHeader();

  const onAddToCartActionVisibilityChanged = (addToCartActionVisible) => {
    setAddToCartActionVisible(addToCartActionVisible);
  };

  const renderCompanyLogo = () => {
    const { homepageLink } = props;
    const logoExists = doesLogoExists();
    const logoVersion = getLogoVersion();
    const companyId = getCompanyId();
    const companyName = getCompanyName();
    if (!logoExists) {
      return null;
    }

    const logoHeight = getLogoHeight();
    const logoWidth = getLogoWidth();
    const logoAspectRatio = logoWidth / logoHeight;

    const logoStyle =
      hideCompanyNameFromCatalogueHeader && centerAlignCompanyNameAndLogoInCatalogueHeader
        ? {
          position: 'absolute',
          left: '50%',
          top: `calc((${NAVBAR_HEIGHT}px - ${NAVBAR_LARGE_LOGO_HEIGHT}px) / 2)`,
          transform: 'translateX(-50%)'
        }
        : {};

    return (
      <div
        className={'logo-icon-container'}
        style={{ ...logoStyle, marginRight: hideCompanyNameFromCatalogueHeader ? 0 : undefined }}
      >
        <a
          target="_self"
          title={companyName ? `${companyName} logo` : 'logo'}
          href={homepageLink}
          className="logo-icon-link"
        >
          <img
            className="logo-icon"
            src={`https://d1h96izmtdkx5o.cloudfront.net/${companyId}.jpg?v=${logoVersion}`}
            alt={companyName ? `${companyName} logo` : 'logo'}
            height={logoHeight}
            width={logoWidth}
            style={{
              height: hideCompanyNameFromCatalogueHeader
                ? NAVBAR_LARGE_LOGO_HEIGHT
                : NAVBAR_LOGO_HEIGHT,
              width:
                logoAspectRatio *
                (hideCompanyNameFromCatalogueHeader
                  ? NAVBAR_LARGE_LOGO_HEIGHT
                  : NAVBAR_LOGO_HEIGHT),
              maxWidth:
                hideCompanyNameFromCatalogueHeader && centerAlignCompanyNameAndLogoInCatalogueHeader
                  ? '300px'
                  : undefined
            }}
          />
        </a>
      </div>
    );
  };

  const renderCompanyName = ({ shouldRenderAsH1Tag = false }) => {
    const { homepageLink } = props;
    const companyName = getCompanyName();
    if (!companyName || hideCompanyNameFromCatalogueHeader) {
      return null;
    }
    const companyNameContent = (
      <a
        target="_self"
        title={companyName}
        href={homepageLink}
        className="companyName"
        style={{ color: companyNameTextColor }}
      >
        {companyName}
      </a>
    );
    return shouldRenderAsH1Tag ? (
      <h1 style={{ margin: '0px' }}>{companyNameContent}</h1>
    ) : (
      companyNameContent
    );
  };

  const menuComponent = isResellerRoute() ? null : <Menu isDesktop={isDesktop} />;

  return (
    <Fragment>
      <div
        // header-collapsed class is also being used to find the bg color for menu.
        className={`header-collapsed${
          showHeaderCollapsedBorder ? ' header-collapsed-with-border' : ''
        }${showHeaderCollapsedShadow && scrollTop > 0 ? ' header-collapsed-with-shadow' : ''}${
          isDesktop ? ' desktop-view-header' : ''
        }`}
        style={{
          backgroundColor:
            currentRoute === ROUTE_NAME.RESELLER_CATALOGUE_GALLERY ||
            currentRoute === ROUTE_NAME.GROUP ||
            currentRoute === ROUTE_NAME.CUSTOM_PAGE ||
            currentRoute === ROUTE_NAME.RESELLER_GLOBAL_REVIEW
              ? homepageTheme.collapsedHeaderBackground
              : headerBackgroundColor,
          height: NAVBAR_HEIGHT,
          ...containerStyle
        }}
      >
        <Fragment>
          <div className="company-details">
            {!isDesktop && menuComponent}
            <BackButton
              currentRoute={currentRoute}
              goBack={goBack}
              showBackForSingleCatalogue={props.showBackForSingleCatalogue}
            />
            {!centerAlignCompanyNameAndLogoInCatalogueHeader && renderCompanyLogo()}
          </div>
          <div
            className={`info ${
              centerAlignCompanyNameAndLogoInCatalogueHeader ? 'info-center' : ''
            }`}
            style={{
              paddingRight:
                hideCompanyNameFromCatalogueHeader && centerAlignCompanyNameAndLogoInCatalogueHeader
                  ? 0
                  : undefined
            }}
          >
            {centerAlignCompanyNameAndLogoInCatalogueHeader && renderCompanyLogo()}
            {currentRoute === 'CUSTOM_PAGE' &&
              (!removeCompanyHeader ||
                (!isGroupRoute() && !shouldRemoveCompanyNameFromCatalogueHeader())) &&
              renderCompanyName({ shouldRenderAsH1Tag: true })}

            {currentRoute !== 'CUSTOM_PAGE' &&
              (!removeCompanyHeader ||
                (!isGroupRoute() && !shouldRemoveCompanyNameFromCatalogueHeader())) &&
              renderCompanyName({ shouldRenderAsH1Tag: false })}

            {currentRoute === ROUTE_NAME.GROUP &&
              allCatalogueIds.length > 0 &&
              !shouldRemoveCatalogueCount() &&
              !centerAlignCompanyNameAndLogoInCatalogueHeader && (
              <div className="catalogueCount" style={{ color: companyNameTextColor }}>
                {I18n.t(allCatalogueIds.length === 1 ? 'GROUP_ITEM_COUNT' : 'GROUP_ITEMS_COUNT', {
                  count: allCatalogueIds.length
                })}
              </div>
            )}
          </div>
          {isDesktop && menuComponent && <nav className="desktop-menu">{menuComponent}</nav>}

          <div className="actions">
            {shouldShowCustomerProfileButton() && (
              <span className="action">
                <CustomerProfile />
              </span>
            )}
            {showSearch &&
              (onSearch ? (
                <span className="action" onClick={onSearch} rel="search">
                  <SearchIcon
                    color={
                      homeGridTheme.searchClass === 'dark' ? '#fff' : homeGridTheme.searchIconColor
                    }
                    size={20}
                  />
                </span>
              ) : (
                <span className="action">
                  <NavBarSearchAction />
                  <NavBarSearchBar />
                </span>
              ))}
            {addToCartRoutes[currentRoute] && (
              <span className={`action ${addToCartActionVisible ? '' : 'hidden'}`}>
                <NavBarReviewAction
                  currentRoute={currentRoute}
                  onVisibilityChanged={onAddToCartActionVisibilityChanged}
                />
              </span>
            )}
          </div>
        </Fragment>
      </div>
    </Fragment>
  );
};

export default HeaderCollapsed;
